// The Review tab's rules that are worth a test: words for a status, the badge, the edit checks
// the server will also run, and the rate line. The server decides everything; these only say it.

import type { ContactContest, ContestWriter, SendDailyRate, SendSettings, SendStep } from '../api/types';

// Amplemarket's per-field limit. The server refuses longer copy rather than truncating it.
export const MAX_FIELD_CHARACTERS = 1024;

const STATUS_WORDS: Record<string, string> = {
  pending: 'Waiting for review',
  sent: 'Sent',
  dry_run: 'Dry run — not pushed',
  held: 'Held',
  failed: 'Failed',
  rejected: 'Rejected',
  pushing: 'Pushing',
  check_amplemarket: 'Check Amplemarket',
  stale: 'Waiting for a redraft',
};

// Rows a human resolves after looking in Amplemarket: an unconfirmed push, or a stuck claim.
export function needsResolve(status: string): boolean {
  return status === 'check_amplemarket' || status === 'pushing';
}

export function statusWord(status: string): string {
  return STATUS_WORDS[status] ?? status;
}

// Semantic tone for the status chip: never the accent colour.
export function statusTone(status: string): 'ok' | 'warn' | 'bad' | 'quiet' {
  if (status === 'sent') return 'ok';
  if (status === 'failed' || status === 'check_amplemarket') return 'bad';
  if (status === 'pushing') return 'warn';
  if (status === 'held' || status === 'dry_run') return 'warn';
  return 'quiet';
}

export const REJECT_REASON_WORDS: Record<string, string> = {  wrong_company: 'Wrong company',
  wrong_person: 'Wrong person',
  copy_wrong: 'Copy is wrong',
  should_go_to_strategist: 'Should go to a strategist',
  never_contact: 'Never contact',
  competitor: 'Competitor',
  other: 'Other',
};

// One contact's push result, as the chip on its card. The codes come from `auto_send/push.py`.
const CONTACT_OUTCOME_WORDS: Record<string, string> = {
  added: 'Added',
  skipped: 'Skipped',
  withheld: 'Withheld',
  unconfirmed: 'Unconfirmed',
  dry_run: 'Dry run',
};

export function contactOutcomeWord(outcome: string): string {
  return CONTACT_OUTCOME_WORDS[outcome] ?? outcome;
}

// A dry run writes the banner's sentence onto every contact; the row already says it once.
export function showContactDetail(outcome: string | null): boolean {
  return outcome !== 'dry_run';
}

// Tiering's "why not tier A" sentence, said in the rules' own terms (`auto_send/tiering.py`).
const NO_RULE_REASON = 'no tier-A rule fired';

export function tierReasonLine(reason: string): string {
  if (reason === NO_RULE_REASON) {
    return 'Auto lane: not voice-native, no paid usage, no recent voice/AI round, not 1,000+ employees with intent.';
  }
  return `Auto lane: ${reason}.`;
}

// The priority score is out of 100 (who ≤40 + did ≤35 + momentum ≤25).
export function priorityLabel(priority: number): string {
  return `priority ${priority}/100`;
}

// What an approve, reject or resolve did to one account, as the line under the banner.
const RESULT_WORDS: Record<string, string> = {
  sent: 'Sent',
  dry_run: 'Approved, dry run',
  held: 'Held',
  failed: 'Failed',
  check_amplemarket: 'Check Amplemarket',
  cap_reached: 'Still queued',
  not_found: 'Not in the queue',
  not_approvable: 'Already decided',
  invalid_edit: 'Edit refused',
  rejected: 'Rejected',
  resolved: 'Resolved',
};

export interface ResultLine {
  word: string;
  detail: string | null;
}

export function resultLine(outcome: string, detail: string | null): ResultLine {
  const word = RESULT_WORDS[outcome] ?? outcome;

  // A reject's detail is the reason code; a resolve's is which answer was given.
  if (outcome === 'rejected') {
    return { word, detail: detail ? (REJECT_REASON_WORDS[detail] ?? detail) : null };
  }
  if (outcome === 'resolved') {
    const said = detail === 'sent'
      ? 'marked as in Amplemarket'
      : 'marked as not sent; it can be approved again';
    return { word, detail: said };
  }

  // A dry run's detail repeats the banner over the page; the card keeps the sequence check.
  if (outcome === 'dry_run') return { word, detail: 'recorded, nothing pushed' };
  return { word, detail };
}

// The question before "Approve all" pushes for real. Null when nothing leaves (a dry run).
export function approveAllQuestion(settings: SendSettings | null, accountCount: number, leadCount: number): string | null {
  if (!settings?.live) return null;
  const accounts = `${accountCount} account${accountCount === 1 ? '' : 's'}`;
  const leads = `${leadCount} lead${leadCount === 1 ? '' : 's'}`;
  const into = (settings.senders?.length ?? 0) > 1
    ? "each account's sender's sequence"
    : `"${settings.sequence_name ?? 'the auto-lane sequence'}"`;
  return `Push ${accounts} (${leads}) into ${into} now? ` +
    'Each one is checked again against the door, the 30-day rule, the exclusion list and the daily caps.';
}

// The badge text: nothing at zero or when the count could not be read, "99+" past two digits.
export function badgeText(pendingCount: number | null | undefined): string | null {
  if (!pendingCount || pendingCount < 1) return null;
  return pendingCount > 99 ? '99+' : String(pendingCount);
}

// Why an edited set of steps would be refused, or null. Mirrors `field_length_problem`.
export function editProblem(steps: SendStep[]): string | null {
  if (steps.length !== 3) return 'A sequence has exactly 3 emails.';
  const subject = (steps[0]?.subject ?? '').trim();
  if (!subject) return 'Email 1 needs a subject.';
  if (subject.length > MAX_FIELD_CHARACTERS) return "Email 1's subject is over 1,024 characters.";
  for (let index = 0; index < steps.length; index += 1) {
    const body = steps[index]?.body ?? '';
    const stepNumber = index + 1;
    if (!body.trim()) return `Email ${stepNumber} is empty.`;
    if (body.length > MAX_FIELD_CHARACTERS) return `Email ${stepNumber} is over 1,024 characters.`;
  }
  return null;
}

export function stepsChanged(before: SendStep[], after: SendStep[]): boolean {
  if (before.length !== after.length) return true;
  return before.some((step, index) => step.body !== after[index]?.body || (step.subject ?? '') !== (after[index]?.subject ?? ''));
}

function percent(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

// One day's readiness line: "8 reviewed · 75% approved as written · 13% edited · 13% rejected".
export function rateLine(rate: SendDailyRate): string {
  if (!rate.review_count) return 'No reviews';
  return `${rate.review_count} reviewed · ${percent(rate.approve_rate)} approved as written · ` +
    `${percent(rate.edit_rate)} edited · ${percent(rate.reject_rate)} rejected`;
}

// The banner over the queue: is an approval going to send, and how much room is left today.
export function modeLine(settings: SendSettings): string {
  const room = Math.max(0, settings.daily_cap - settings.sent_today);
  const leadRoom = Math.max(0, settings.daily_lead_cap - settings.leads_today);
  if (!settings.live) {
    return `${settings.dry_run_reason ?? 'Dry run.'} Approvals are recorded and every rail runs.`;
  }
  if (settings.sender_config_error) {
    return `Live, but SCOUT_AUTO_SENDERS cannot be read, so nothing will push: ${settings.sender_config_error}`;
  }
  if (!settings.sequence_configured) {
    return 'Live, but no sequence is configured: set SCOUT_AUTO_SEQUENCE_ID and SCOUT_AUTO_SEQUENCE_NAME.';
  }
  const into = (settings.senders?.length ?? 0) > 1
    ? "each account's sender's sequence"
    : `"${settings.sequence_name}"`;
  return `Live — approving pushes into ${into}. Today: ${settings.sent_today} of ` +
    `${settings.daily_cap} accounts (${room} left), ${settings.leads_today} of ${settings.daily_lead_cap} ` +
    `leads (${leadRoom} left).`;
}

// Who sends, by share: "kelly@… 75% → "Seq" · eva@… 25% → "Seq (Eva)"". Null with one sender.
export function sendersLine(settings: SendSettings | null): string | null {
  const senders = settings?.senders ?? [];
  if (senders.length < 2) return null;
  const totalWeight = senders.reduce((total, sender) => total + sender.weight, 0);
  const parts = senders.map(sender => {
    const share = totalWeight ? Math.round((sender.weight / totalWeight) * 100) : 0;
    const cap = sender.daily_lead_cap !== null ? ` (${sender.leads_today}/${sender.daily_lead_cap} leads today)` : '';
    return `${sender.email} ${share}% → "${sender.sequence_name ?? 'no sequence'}"${cap}`;
  });
  return `Senders: ${parts.join(' · ')}`;
}

// The tone of a row's SDR-owner note after a live push. A failure is the one that needs a person.
export function ownerTone(outcome: string | null | undefined): 'ok' | 'warn' | 'bad' {
  if (outcome === 'failed') return 'bad';
  if (outcome === 'set' || outcome === 'already_sender') return 'ok';
  return 'warn';
}

// The contest's scores, winner first then highest: "engineer 8 (winner) · human 7 · insider 5".
export function contestScoresLine(contest: ContactContest): string {
  const entries = Object.entries(contest.buyer.scores);
  entries.sort((left, right) => {
    if (left[0] === contest.buyer.winner) return -1;
    if (right[0] === contest.buyer.winner) return 1;
    return right[1] - left[1];
  });
  return entries
    .map(([persona, score]) => `${persona} ${score}${persona === contest.buyer.winner ? ' (winner)' : ''}`)
    .join(' · ');
}

// Every writer but the winner, highest score first, for the "losing sequences" toggle.
export function losingWriters(contest: ContactContest): ContestWriter[] {
  const losers = contest.writers.filter(writer => writer.persona !== contest.buyer.winner);
  losers.sort((left, right) => (contest.buyer.scores[right.persona] ?? 0) - (contest.buyer.scores[left.persona] ?? 0));
  return losers;
}

// The banner line for the contest, the bot and the size limit: how rows reach this page tonight.
export function contestLine(settings: SendSettings): string | null {
  if (settings.contest_on === undefined) return null;
  const minScore = settings.contest_min_score ?? 0;
  const bar = minScore > 0 ? ` and a buyer score of ${minScore}+` : '';
  const contest = settings.contest_on
    ? `Writers' contest on (goes out when Scout says sendable${bar})`
    : "Writers' contest off (single drafts)";
  const bot = settings.auto_approve_on
    ? `bot approval on, up to ${settings.auto_approve_nightly_cap ?? 50} a night`
    : 'bot approval off';
  const maxEmployees = settings.max_auto_employees ?? 0;
  const size = maxEmployees > 0 ? ` · ${maxEmployees.toLocaleString('en-US')}+ employees go to a strategist` : '';
  return `${contest} · ${bot}${size}.`;
}

// Scout's verdict on the final copy: "Sendable — reason", "Not sendable — reason", or null (older Scout).
export function sendableLine(contest: ContactContest): string | null {
  if (contest.sendable === undefined || contest.sendable === null) return null;
  const word = contest.sendable ? 'Sendable' : 'Not sendable';
  return contest.sendable_reason ? `${word} — ${contest.sendable_reason}` : word;
}

// Where the final three emails came from: "Final: stitched (1 engineer · 2 human · 3 insider), 9/10, coherent".
export function finalSourceLine(contest: ContactContest): string | null {
  const source = contest.final_source ?? '';
  if (!source) return null;
  const sources = contest.position_sources ?? [];
  const positions = sources.length ? ` (${sources.map((persona, index) => `${index + 1} ${persona}`).join(' · ')})` : '';
  if (source !== 'stitched') return `Final: ${source}'s whole sequence${positions}`;
  const score = contest.stitched_score != null ? `, ${contest.stitched_score}/10` : '';
  const coherent = contest.stitched_coherent === true ? ', coherent'
    : contest.stitched_coherent === false ? ', not coherent' : '';
  return `Final: stitched${positions}${score}${coherent}`;
}

// Each position's scores, highest first, the persona used marked: "Email 1: engineer 8 (used) · human 6".
export function positionScoreLines(contest: ContactContest): string[] {
  const positionScores = contest.position_scores ?? {};
  const sources = contest.position_sources ?? [];
  const lines: string[] = [];
  for (const position of Object.keys(positionScores).sort()) {
    const entries = Object.entries(positionScores[position] ?? {});
    entries.sort((left, right) => right[1] - left[1]);
    const used = sources[Number(position) - 1];
    const scores = entries
      .map(([persona, score]) => `${persona} ${score}${persona === used ? ' (used)' : ''}`)
      .join(' · ');
    lines.push(`Email ${position}: ${scores}`);
  }
  return lines;
}

// Both caps, for the queue page's header line whatever the mode.
export function capsLine(settings: SendSettings): string {
  return `Daily caps: ${settings.sent_today}/${settings.daily_cap} accounts · ` +
    `${settings.leads_today}/${settings.daily_lead_cap} leads`;
}
